import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Download, Loader2, CheckCircle2 } from "lucide-react";
import { API, authHeaders, errorText } from "@/lib/api";

const fmt = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);

const Row = ({ f, reload }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1048576) return toast.error("Max file size is 25 MB.");
    setBusy(true);
    const body = new FormData();
    body.append("file", file);
    try {
      await axios.post(`${API}/admin/toolkit-files/${f.key}`, body, { headers: authHeaders() });
      toast.success(`${f.title} uploaded`);
      reload();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/admin/toolkit-files/${f.key}`, { headers: authHeaders() }); toast.success("File removed"); reload(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const download = async () => {
    try {
      const r = await axios.get(`${API}/admin/toolkit-files/${f.key}`, { headers: authHeaders(), responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div data-testid={`admin-file-row-${f.key}`} className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
      <div className="flex-1">
        <p className="font-semibold text-[#0B1B33]">{f.title}</p>
        {f.filename ? (
          <p className="flex items-center gap-1.5 text-[13px] text-emerald-700"><CheckCircle2 className="h-4 w-4" />{f.filename} · {fmt(f.size)} · {new Date(f.uploaded_at).toLocaleDateString()}</p>
        ) : <p className="text-[13px] text-slate-600">Not uploaded yet. Shown as "Coming soon" to sign-ups.</p>}
      </div>
      <div className="flex gap-2">
        <label data-testid={`admin-file-upload-${f.key}`} className="btn-teal flex cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-[14px] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {f.filename ? "Replace" : "Upload"}
          <input type="file" className="hidden" data-testid={`admin-file-input-${f.key}`} onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        {f.filename && <>
          <button data-testid={`admin-file-download-${f.key}`} onClick={download} className="rounded-lg border border-slate-300 p-2 text-slate-700 hover:bg-slate-50" aria-label="Download"><Download className="h-4 w-4" /></button>
          <button data-testid={`admin-file-delete-${f.key}`} onClick={remove} className="rounded-lg border border-slate-300 p-2 text-rose-600 hover:bg-rose-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
        </>}
      </div>
    </div>
  );
};

export const ToolkitFiles = () => {
  const [files, setFiles] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/admin/toolkit-files`, { headers: authHeaders() })
      .then((r) => setFiles(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(errorText(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      <p className="text-[14px] text-slate-600">Upload your AI Book here. Every sign-up, past and future, sees it on their personal toolkit page (linked in their email). The other five toolkit guides are written by AI and tailored to each sign-up's role, experience and goal automatically, and the AI Survey PDF comes from the diagnostic.</p>
      <div data-testid="admin-toolkit-files" className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
        {files.map((f) => <Row key={f.key} f={f} reload={load} />)}
      </div>
    </div>
  );
};
